import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../lib/api";
import { formatMoney } from "../lib/money";
import {
  FEE_ESTADO_LABEL,
  organizerFeesApi,
  shortDate,
  type FeeLedger,
  type FeeOrganizerList,
} from "../lib/organizerFeesApi";
import "../components/organizerFees.css";

const FILTERS: Array<{ key: string; label: string }> = [
  { key: "all", label: "Todos" },
  { key: "con_saldo", label: "Con saldo" },
  { key: "al_corriente", label: "Al corriente" },
  { key: "vencidos", label: "Vencidos" },
];

const MOVEMENT_LABEL: Record<string, string> = {
  charge: "Cargo",
  credit: "Crédito",
  payment: "Pago recibido",
  payment_reversal: "Pago revertido",
  adjustment: "Ajuste",
  block: "Bloqueo",
  unblock: "Desbloqueo",
  publish_exception: "Permiso para publicar",
  publish_exception_used: "Permiso usado",
  beca_approved: "Beca aprobada",
  beca_rejected: "Beca rechazada",
};

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** Superadmin › Fees de organizadores (maqueta Fees-Deuda, parte B). */
export default function SuperadminOrganizerFeesPage() {
  const { token } = useAuth();
  const [list, setList] = useState<FeeOrganizerList | null>(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<FeeLedger | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: "", reference: "", tournament: "" });
  const [adj, setAdj] = useState({ amount: "", reason: "", tournament: "" });
  const [blockReason, setBlockReason] = useState("");
  const [exceptionReason, setExceptionReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState<Record<number, string>>({});
  const detailRef = useRef<HTMLElement | null>(null);

  const loadList = useCallback(async () => {
    if (!token) return;
    try {
      const response = await organizerFeesApi.list(token, { filter, q: q.trim() });
      setList(response.data);
      setError(null);
    } catch (err) {
      setError(errorText(err, "No se pudo cargar la lista."));
    }
  }, [token, filter, q]);

  useEffect(() => {
    const handle = window.setTimeout(() => void loadList(), 250);
    return () => window.clearTimeout(handle);
  }, [loadList]);

  const loadDetail = useCallback(
    async (organizerId: number) => {
      if (!token) return;
      try {
        const response = await organizerFeesApi.detail(token, organizerId);
        setDetail(response.data);
      } catch (err) {
        setError(errorText(err, "No se pudo cargar el detalle."));
      }
    },
    [token],
  );

  function openDetail(organizerId: number) {
    setSelected(organizerId);
    setDetail(null);
    setMessage(null);
    setPay({ amount: "", reference: "", tournament: "" });
    setAdj({ amount: "", reason: "", tournament: "" });
    setBlockReason("");
    setExceptionReason("");
    void loadDetail(organizerId).then(() => detailRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }));
  }

  async function run(action: () => Promise<{ data: FeeLedger } | unknown>, ok: string) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = (await action()) as { data?: FeeLedger };
      if (response?.data && "movements" in response.data) setDetail(response.data);
      else if (selected !== null) await loadDetail(selected);
      setMessage(ok);
      await loadList();
      return true;
    } catch (err) {
      setError(errorText(err, "No se pudo guardar."));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function recalculate() {
    if (!token) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = await organizerFeesApi.recalculate(token);
      setList(response.data);
      if (selected !== null) await loadDetail(selected);
      setMessage("Saldos recalculados.");
    } catch (err) {
      setError(errorText(err, "No se pudo recalcular."));
    } finally {
      setBusy(false);
    }
  }

  async function downloadExcel() {
    if (!token) return;
    try {
      const response = await organizerFeesApi.downloadXlsx(token, { filter, q: q.trim() });
      if (!response.ok) throw new Error("excel");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "fees-organizadores.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("No se pudo descargar el Excel.");
    }
  }

  const totals = list?.totals;
  const tournamentsOptions = detail?.tournaments.filter((row) => row.tournament_id !== null) ?? [];

  return (
    <div className="adm-players safees">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Superadmin · fees de plataforma</span>
          <h1 className="adm-players__title">Lo que deben los organizadores</h1>
          <p className="muted sa-section-sub">
            Solo inscripciones cobradas por fuera. Lo de Mercado Pago ya entró solo.
          </p>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--secondary sa-link-btn" to="/superadmin/fees">
            Fee por torneo
          </Link>
          <Link className="adm-players__btn adm-players__btn--secondary sa-link-btn" to="/superadmin">
            Volver
          </Link>
        </div>
      </header>

      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {message && selected === null ? <p className="form-message" role="status">{message}</p> : null}

      <div className="ofees__kpis">
        <div className="ofees__kpi ofees__kpi--navy">
          <small>Por cobrar a organizadores</small>
          <strong>{formatMoney(totals?.por_cobrar ?? 0)}</strong>
        </div>
        <div className="ofees__kpi">
          <small>Vencido</small>
          <strong>{formatMoney(totals?.vencido ?? 0)}</strong>
        </div>
        <div className="ofees__kpi">
          <small>Cobrado este mes</small>
          <strong>{formatMoney(totals?.cobrado_mes ?? 0)}</strong>
        </div>
      </div>
      {totals && totals.becas_pending > 0 ? (
        <p className="ofees__warn">
          {totals.becas_pending === 1 ? "1 beca o cortesía espera tu revisión." : `${totals.becas_pending} becas o cortesías esperan tu revisión.`}
        </p>
      ) : null}
      {list?.payment_reviews && list.payment_reviews.length > 0 ? (
        <div className="ofees__warn" role="status">
          <strong>Avisos de pago de Mercado Pago en revisión (no se aplicaron):</strong>
          <ul>
            {list.payment_reviews.map((review) => (
              <li key={review.event_id}>
                Pago {review.event_id.split(":")[0]} · {review.event_type} · {review.reason}
                {review.date ? ` · ${shortDate(review.date)}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <section className="card ofees">
        <div className="safees__filters">
          {FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`safees__pill${filter === item.key ? " is-active" : ""}`}
              aria-pressed={filter === item.key}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
          <input
            className="safees__search"
            type="search"
            aria-label="Buscar organizador"
            placeholder="Buscar organizador o correo"
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
          <button
            type="button"
            className="ofees__btn ofees__btn--sm"
            disabled={busy}
            title="Guarda los cargos de todos los torneos con las inscripciones de hoy"
            onClick={() => void recalculate()}
          >
            Recalcular
          </button>
          <button type="button" className="ofees__btn ofees__btn--sm" onClick={() => void downloadExcel()}>
            Exportar Excel
          </button>
        </div>
        {list && list.rows.length === 0 ? <p className="ofees__muted">No hay organizadores con este filtro.</p> : null}
        {list && list.rows.length > 0 ? (
          <div className="ofees__table-wrap">
            <table className="ofees__table" data-sa-fees-list>
              <thead>
                <tr>
                  <th>Organizador</th>
                  <th>Responsable</th>
                  <th>Torneos</th>
                  <th>Último pago</th>
                  <th>Saldo</th>
                  <th>Estado</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {list.rows.map((row) => (
                  <tr key={row.organizer_user_id}>
                    <td className="ofees__name">{row.organizer_name}</td>
                    <td data-label="Responsable"><span>{row.responsible_name}</span></td>
                    <td data-label="Torneos"><span>{row.tournaments}</span></td>
                    <td data-label="Último pago">
                      <span>{row.last_payment ? `${formatMoney(row.last_payment.amount)} · ${shortDate(row.last_payment.date)}` : "—"}</span>
                    </td>
                    <td data-label="Saldo"><span className="ofees__amt">{formatMoney(row.saldo)}</span></td>
                    <td data-label="Estado">
                      <span>
                        <span className={`ofees__chip ofees__chip--${row.estado}`}>{FEE_ESTADO_LABEL[row.estado]}</span>
                        {row.blocked ? <> <span className="ofees__chip ofees__chip--blocked">Bloqueado</span></> : null}
                      </span>
                    </td>
                    <td data-label="">
                      <button
                        type="button"
                        className="ofees__btn ofees__btn--sm"
                        onClick={() => openDetail(row.organizer_user_id)}
                        aria-label={`Ver detalle de ${row.organizer_name}`}
                      >
                        Ver detalle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {selected !== null && detail ? (
        <section className="card ofees" ref={detailRef} data-sa-fees-detail aria-labelledby="safees-detail-title">
          <div className="ofees__head">
            <div>
              <h2 className="ofees__title" id="safees-detail-title">
                {detail.organizer_name} · saldo {formatMoney(Math.max(Number(detail.saldo), 0))}
              </h2>
              <p className="ofees__muted">
                {detail.responsible_name} · {detail.tournaments.length} torneo{detail.tournaments.length === 1 ? "" : "s"} · último pago{" "}
                {detail.last_payment ? `${formatMoney(detail.last_payment.amount)} el ${shortDate(detail.last_payment.date)}` : "—"}
              </p>
            </div>
            <span className={`ofees__chip ofees__chip--${detail.estado}`}>{FEE_ESTADO_LABEL[detail.estado]}</span>
          </div>
          {message ? <p className="form-message" role="status">{message}</p> : null}

          <div className="safees__grid">
            <form
              className="safees__panel"
              onSubmit={(event) => {
                event.preventDefault();
                if (!token) return;
                void run(
                  () =>
                    organizerFeesApi.registerPayment(token, selected, {
                      amount: pay.amount.trim(),
                      reference: pay.reference.trim(),
                      tournament_id: pay.tournament ? Number(pay.tournament) : null,
                    }),
                  "Pago registrado.",
                ).then((ok) => ok && setPay({ amount: "", reference: "", tournament: "" }));
              }}
            >
              <h3>Registrar pago</h3>
              <p className="ofees__note">Referencia obligatoria. Queda en el historial con quién y cuándo.</p>
              <div className="safees__form">
                <label>
                  Monto
                  <input inputMode="decimal" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} required />
                </label>
                <label>
                  Referencia
                  <input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} required />
                </label>
                <label>
                  Aplicar a
                  <select value={pay.tournament} onChange={(e) => setPay({ ...pay, tournament: e.target.value })}>
                    <option value="">Saldo total (los más viejos primero)</option>
                    {tournamentsOptions.map((row) => (
                      <option key={row.tournament_id} value={String(row.tournament_id)}>{row.tournament_name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="ofees__bar">
                <button type="submit" className="ofees__btn ofees__btn--lime" disabled={busy}>Registrar pago</button>
              </div>
            </form>

            <form
              className="safees__panel"
              onSubmit={(event) => {
                event.preventDefault();
                if (!token) return;
                void run(
                  () =>
                    organizerFeesApi.adjust(token, selected, {
                      amount: adj.amount.trim(),
                      reason: adj.reason.trim(),
                      tournament_id: adj.tournament ? Number(adj.tournament) : null,
                    }),
                  "Ajuste guardado.",
                ).then((ok) => ok && setAdj({ amount: "", reason: "", tournament: "" }));
              }}
            >
              <h3>Ajuste o condonación</h3>
              <p className="ofees__note">Monto positivo baja el saldo (condonar); negativo lo sube. Motivo obligatorio.</p>
              <div className="safees__form">
                <label>
                  Monto
                  <input inputMode="decimal" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} required />
                </label>
                <label>
                  Motivo
                  <input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} required />
                </label>
                <label>
                  Torneo
                  <select value={adj.tournament} onChange={(e) => setAdj({ ...adj, tournament: e.target.value })}>
                    <option value="">Saldo total</option>
                    {tournamentsOptions.map((row) => (
                      <option key={row.tournament_id} value={String(row.tournament_id)}>{row.tournament_name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="ofees__bar">
                <button type="submit" className="ofees__btn" disabled={busy}>Guardar ajuste</button>
              </div>
            </form>

            <div className="safees__panel">
              <h3>Bloquear por falta de pago</h3>
              <p className="ofees__note">
                Bloqueado no puede crear, duplicar ni publicar torneos. Los torneos ya publicados siguen igual.
              </p>
              {detail.control.blocked ? (
                <p className="ofees__danger">Bloqueado: {detail.control.blocked_reason}</p>
              ) : (
                <div className="safees__form">
                  <label>
                    Motivo
                    <input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />
                  </label>
                </div>
              )}
              <label className="safees__switch">
                <input
                  type="checkbox"
                  role="switch"
                  checked={detail.control.blocked}
                  disabled={busy || (!detail.control.blocked && !blockReason.trim())}
                  onChange={(e) => {
                    if (!token) return;
                    const blocked = e.target.checked;
                    void run(
                      () => organizerFeesApi.setBlock(token, selected, { blocked, reason: blocked ? blockReason.trim() : null }),
                      blocked ? "Cuenta bloqueada por falta de pago." : "Cuenta desbloqueada.",
                    ).then(() => setBlockReason(""));
                  }}
                />
                {detail.control.blocked ? "Bloqueado" : "Bloquear por falta de pago"}
              </label>
            </div>

            <form
              className="safees__panel"
              onSubmit={(event) => {
                event.preventDefault();
                if (!token) return;
                void run(
                  () => organizerFeesApi.grantException(token, selected, exceptionReason.trim()),
                  "Puede publicar un torneo aunque tenga saldo.",
                ).then((ok) => ok && setExceptionReason(""));
              }}
            >
              <h3>Permitir publicar (una vez)</h3>
              <p className="ofees__note">
                {detail.control.exception_available
                  ? `Ya tiene un permiso sin usar: ${detail.control.exception_reason ?? ""}`
                  : "Deja publicar UN torneo nuevo aunque tenga saldo de torneos cerrados."}
              </p>
              <div className="safees__form">
                <label>
                  Motivo
                  <input value={exceptionReason} onChange={(e) => setExceptionReason(e.target.value)} required />
                </label>
              </div>
              <div className="ofees__bar">
                <button type="submit" className="ofees__btn" disabled={busy}>Permitir publicar</button>
              </div>
            </form>
          </div>

          <div className="ofees__bar">
            <button type="button" className="ofees__btn ofees__btn--sm" disabled>Recordar por WhatsApp</button>
            <button type="button" className="ofees__btn ofees__btn--sm" disabled>Recordar por correo</button>
            <span className="ofees__chip ofees__chip--later">Después · requiere autorización</span>
          </div>

          {detail.becas.length > 0 ? (
            <div className="safees__panel" data-sa-becas>
              <h3>Becas y cortesías</h3>
              {detail.becas.map((beca) => (
                <div key={beca.charge_id} className="safees__beca">
                  <div>
                    <strong>{beca.tournament_name}</strong> · {beca.player_name ?? `orden #${beca.order_id ?? "—"}`} ·{" "}
                    {formatMoney(beca.amount)} · motivo: {beca.reason}
                    {beca.status !== "pending" ? (
                      <> · <strong>{beca.status === "approved" ? "Aprobada" : "Rechazada"}</strong>{beca.review_note ? ` (${beca.review_note})` : ""}</>
                    ) : null}
                  </div>
                  {beca.status === "pending" ? (
                    <div className="ofees__bar">
                      <button
                        type="button"
                        className="ofees__btn ofees__btn--sm"
                        disabled={busy}
                        onClick={() => token && void run(() => organizerFeesApi.reviewBeca(token, beca.charge_id, "approve"), "Beca aprobada.")}
                      >
                        Aprobar
                      </button>
                      <input
                        className="safees__search"
                        aria-label="Motivo del rechazo"
                        placeholder="Motivo del rechazo"
                        value={rejectNotes[beca.charge_id] ?? ""}
                        onChange={(e) => setRejectNotes({ ...rejectNotes, [beca.charge_id]: e.target.value })}
                      />
                      <button
                        type="button"
                        className="ofees__btn ofees__btn--sm ofees__btn--danger"
                        disabled={busy || !(rejectNotes[beca.charge_id] ?? "").trim()}
                        onClick={() =>
                          token &&
                          void run(
                            () => organizerFeesApi.reviewBeca(token, beca.charge_id, "reject", rejectNotes[beca.charge_id]),
                            "Beca rechazada: ahora es cargo.",
                          )
                        }
                      >
                        Rechazar
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          <div className="ofees__table-wrap">
            <table className="ofees__table" data-sa-ledger>
              <thead>
                <tr>
                  <th>Movimiento</th>
                  <th>Fecha</th>
                  <th>Detalle</th>
                  <th>Monto</th>
                </tr>
              </thead>
              <tbody>
                {detail.movements.map((movement, index) => (
                  <tr key={`${movement.kind}-${index}`}>
                    <td className="ofees__name">{MOVEMENT_LABEL[movement.kind] ?? movement.kind}</td>
                    <td data-label="Fecha"><span>{shortDate(movement.date)}</span></td>
                    <td data-label="Detalle"><span>{movement.detail}</span></td>
                    <td data-label="Monto">
                      <span className="ofees__amt">
                        {movement.amount === null ? "—" : `${Number(movement.amount) > 0 ? "+" : "−"}${formatMoney(Math.abs(Number(movement.amount)))}`}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="ofees__name">Saldo</td>
                  <td data-label="" />
                  <td data-label="" />
                  <td data-label="Saldo"><span className="ofees__amt">{formatMoney(detail.saldo)}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
